/** Local calendar date as YYYY-MM-DD (for `<input type="date">`). */
function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Compute a pause-until date N months from today. */
export function pauseUntilMonthsFromToday(months: number): string {
  return formatLocalYmd(addMonths(startOfToday(), months));
}

/** Compute an extended pause date: current paused_until + N months.
 *  Accepts ISO-8601 ("2026-05-15T00:00:00.000000Z") or bare "YYYY-MM-DD". */
export function extendPauseByMonths(
  currentPausedUntil: string,
  months: number,
): string {
  const ymd = currentPausedUntil.slice(0, 10);
  const [y, m, d] = ymd.split("-").map(Number);
  const base = new Date(y, m - 1, d);
  return formatLocalYmd(addMonths(base, months));
}

/** Add a billing interval (e.g. 1 "month", 2 "week") to a date.
 *  Accepts ISO-8601 ("2026-05-15T00:00:00.000000Z") or bare "YYYY-MM-DD";
 *  returns "YYYY-MM-DD". Unknown units fall back to months. */
export function addBillingInterval(
  dateStr: string,
  intervalUnit: string,
  intervalCount: number,
): string {
  const ymd = dateStr.slice(0, 10);
  const [y, m, d] = ymd.split("-").map(Number);
  const base = new Date(y, m - 1, d);
  const unit = intervalUnit.trim().toLowerCase();
  const n = Math.max(1, Math.round(intervalCount));
  if (unit === "day" || unit === "days") return formatLocalYmd(addDays(base, n));
  if (unit === "week" || unit === "weeks")
    return formatLocalYmd(addDays(base, n * 7));
  if (unit === "year" || unit === "years")
    return formatLocalYmd(addMonths(base, n * 12));
  return formatLocalYmd(addMonths(base, n));
}

/** Whole-month difference between lastChargeDate and today. */
function monthsSinceLastCharge(lastChargeDate: string): number {
  const ymd = lastChargeDate.slice(0, 10);
  const [y, m, d] = ymd.split("-").map(Number);
  const charge = new Date(y, m - 1, d);
  const today = startOfToday();
  const diffMonths =
    (today.getFullYear() - charge.getFullYear()) * 12 +
    (today.getMonth() - charge.getMonth());
  // If today's day-of-month is before the charge day, we haven't completed the month yet.
  if (today.getDate() < charge.getDate()) {
    return diffMonths - 1;
  }
  return diffMonths;
}

/** Which pause month options (1 and/or 2) are allowed given the last charge age. */
export function getAllowedPauseMonths(
  lastChargeDate?: string | null,
): number[] {
  if (!lastChargeDate) return [1, 2];
  const months = monthsSinceLastCharge(lastChargeDate);
  const allowed: number[] = [];
  if (months <= 4) allowed.push(1);
  if (months <= 3) allowed.push(2);
  return allowed;
}

/** Which extend-pause month options (1 and/or 2) are allowed.
 *  Checks whether the resulting date (nextBillingDate + N months)
 *  stays within 5 months of the last charge date. */
export function getAllowedExtendPauseMonths(
  nextBillingDate?: string | null,
  lastChargeDate?: string | null,
): number[] {
  if (!lastChargeDate || !nextBillingDate) return [1, 2];
  const chargeYmd = lastChargeDate.slice(0, 10);
  const [cy, cm, cd] = chargeYmd.split("-").map(Number);
  const chargeDate = new Date(cy, cm - 1, cd);
  const maxAllowed = addMonths(chargeDate, 5);

  const billingYmd = nextBillingDate.slice(0, 10);
  const [by, bm, bd] = billingYmd.split("-").map(Number);
  const billing = new Date(by, bm - 1, bd);

  const allowed: number[] = [];
  if (addMonths(billing, 1) <= maxAllowed) allowed.push(1);
  if (addMonths(billing, 2) <= maxAllowed) allowed.push(2);
  return allowed;
}

/**
 * Age-based max date for calendars (change delivery date, unpause).
 * Returns null when the action should be hidden entirely (> 4 months).
 */
export function getAgeBasedMaxDate(
  lastChargeDate?: string | null,
): string | null {
  if (!lastChargeDate) {
    return formatLocalYmd(addMonths(startOfToday(), 2));
  }
  const months = monthsSinceLastCharge(lastChargeDate);
  if (months > 4) return null;
  if (months > 3) return formatLocalYmd(addMonths(startOfToday(), 1));
  return formatLocalYmd(addMonths(startOfToday(), 2));
}

/**
 * Delivery date bounds using the age-based max.
 * Returns null when the calendar should be hidden entirely.
 */
export function getAgeBasedDeliveryDateBounds(
  lastChargeDate?: string | null,
): { min: string; max: string } | null {
  const max = getAgeBasedMaxDate(lastChargeDate);
  if (!max) return null;
  const min = formatLocalYmd(addDays(startOfToday(), 1));
  return { min, max };
}

export const DELAY_PRESET_WEEKS = [2, 3, 6] as const;

/**
 * Delay presets: next_billing + N weeks. Always offered whenever there's a next
 * billing date — delay is intentionally short (2/3/6 wks), so it is no longer
 * gated by the age-based reschedule window (unlike change-delivery / unpause).
 * `_lastChargeDate` is kept for call-site compatibility but no longer used.
 */
export function getDelayPresets(
  nextBillingDate?: string | null,
  _lastChargeDate?: string | null,
): { weeks: number; date: string }[] {
  if (!nextBillingDate) return [];
  return DELAY_PRESET_WEEKS.map((weeks) => ({
    weeks,
    date: addBillingInterval(nextBillingDate, "week", weeks),
  }));
}
